import { forEach, forOwn, map } from 'lodash';
import { Magnitude } from 'gear';

export const Xml = {};

Xml.fromString = function( string ) {
  const xml = (new DOMParser()).parseFromString(string, 'text/xml');

  if(!xml || !xml.documentElement || xml.getElementsByTagName('parsererror').length) {
    console.log('Invalid XML: ' + string);
  }

  return xml;
};

Xml.toString = function(v) {
  return (new XMLSerializer()).serializeToString(v);
};

Xml.createEmptyDomDocument = function() {
  const doc = Xml.fromString('<dummy/>');
  doc.removeChild(doc.documentElement);
  return doc;
};

Xml.appendElement = function (parentElement, elementName) {
  const element = (parentElement.ownerDocument || parentElement).createElement(elementName);
  parentElement.appendChild(element);
  return element;
};

const children_ = Xml.children_ = function( node ) {
  return node.children||node.childNodes;
};

Xml.children = function( node, tag ) {
  const r = [];
  const c = children_(node);
  for( let k = 0, kEnd = c.length; k < kEnd; ++k ) {
    const ck = c[k];
    if( ck.nodeName === tag ) {
      r.push( ck );
    }
  }
  return r;
};

Xml.child = function( node, tag ) {
  const c = children_(node);
  for( let k = 0, kEnd = c.length; k < kEnd; ++k ) {
    const ck = c[k];
    if( ck.nodeName === tag ) {
      return ck;
    }
  }
};

function floatArrayFromXmlString(s, separator) {
  if(separator === undefined) {
    separator = ',';
  }
  const sepVals = s.split(separator);
  return map(sepVals,parseFloat);
}

function floatArrayToXmlString(fa, separator) {
  if(separator === undefined) {
    separator = ',';
  }
  const vals = map( fa, v => (+v.toFixed(4)).toString() );
  return vals.join(separator);
}

// We should support null pens and brushes, like in Cx.Engine?

Xml.fontToXml = function (font) {
  const xmlNode = Xml.createEmptyDomDocument();
  font.buildXml(xmlNode);
  const xml = Xml.toString(xmlNode);
  return xml;
};

Xml.penToXml = function (pen) {
  const xmlNode = Xml.createEmptyDomDocument();
  pen.buildXml(xmlNode);
  const xml = Xml.toString(xmlNode);
  return xml;
};

Xml.brushToXml = function (brush) {
  const xmlNode = Xml.createEmptyDomDocument();
  brush.buildXml(xmlNode);
  const xml = Xml.toString(xmlNode);
  return xml;
};

Xml.getFloatAttr = function (node, tag, defaultValue) {
  const value = parseFloat(node.getAttribute(tag));
  return isNaN(value) ? defaultValue : value;
};
Xml.getIntAttr = function (node, tag, defaultValue) {
  const value = parseInt(node.getAttribute(tag));
  return isNaN(value) ? defaultValue : value;
};
Xml.getBoolAttr = function (node, tag, defaultValue) {
  const value = node.getAttribute(tag);
  if( value ) {
    return value !== '0' && value.toLowerCase() !== 'false' ;
  }
  return defaultValue ;
};

Xml.getStringArrayAttr = function(node, tag, defaultValue) {
  const valueString = node.getAttribute(tag) ;
  if( valueString ) {
    const separator = ',';
    return valueString.split(separator);
  }
  return defaultValue ;
};

Xml.getFloatArrayAttr = function(node, tag, defaultValue) {
  const valueString = node.getAttribute(tag) ;
  if( valueString ) {
    return floatArrayFromXmlString(valueString);
  }
  return defaultValue ;
};

Xml.setFloatAttr = function( node, tag, value ) {
  node.setAttribute(tag,+value.toFixed(4));
};
Xml.setIntAttr = function( node, tag, value ) {
  node.setAttribute(tag,value.toFixed(0));
};
Xml.setBoolAttr = function( node, tag, value ) {
  node.setAttribute(tag,value?'true':'false');
};
Xml.setFloatArrayAttr = function( node, tag, value ) {
  node.setAttribute(tag,floatArrayToXmlString(value));
};

Xml.setStringArrayAttr = function( node, tag, value ) {
  const separator = ',';
  const s = value.join(separator);
  node.setAttribute(tag,s);
};

Xml.makeNodeParser = function(obj) {
  return {
    decode( node, tag, v ) {
      return obj.fromXml(node) || v;
    },
    encode( node, tag, v ) {
      v.buildXml(node);
    }
  };
};
Xml.makeAttrParser = function(obj) {
  return {
    decode( node, tag, v ) {
      return obj.parseXml(node) || v;
    },
    encode( node, tag, v ) {
      v.setupXml(node);
    }
  };
};
Xml.makeObjectParser = function(obj) {
  return obj.fromXml ? Xml.makeNodeParser(obj) : Xml.makeAttrParser(obj);
};

Xml.makeArrayObjectParser = function(obj) {
  return {
    decode( node, tag, v ) {
      return obj.arrayFromXml(node) || v ;
    },
    encode( node, tag, v ) {
      return obj.arrayBuildXml ? obj.arrayBuildXml(node,v) : Xml.addObjectArray(node,v);
    }
  };
};

// Xml.Parser
//---------------------------------------------------------------------------

Xml.Parser = {
  'number'   : { decode( node, tag, v ) { return Xml.getFloatAttr   (node,tag,v); } ,
                 encode( node, tag, v ) {        Xml.setFloatAttr   (node,tag,v); } },
  'integer'  : { decode( node, tag, v ) { return Xml.getIntAttr     (node,tag,v); } ,
                 encode( node, tag, v ) {        Xml.setIntAttr     (node,tag,v); } },
  'magnitude': { decode( node, tag, v ) { return Magnitude.parse(node.getAttribute(tag)) || v; } ,
                 encode( node, tag, v ) {        if (v != null) 
                                                   if (v instanceof Magnitude) node.setAttribute(tag,v.toString()); 
                                                   else                        node.setAttribute(tag,(new Magnitude(v,'mm')).toString()); } },
  'string'   : { decode( node, tag, v ) { return node.getAttribute(tag) || v; } ,
                 encode( node, tag, v ) {        node.setAttribute(tag,v); } },
  'boolean'  : { decode( node, tag, v ) { return Xml.getBoolAttr    (node,tag,v); } ,
                 encode( node, tag, v ) {        Xml.setBoolAttr    (node,tag,v); } },
  'angle'    : { decode( node, tag, v ) { return Xml.getFloatAttr   (node,tag,v); } ,
                 encode( node, tag, v ) {        Xml.setFloatAttr   (node,tag,v); } },
  'number[]' : { decode( node, tag, v ) { return Xml.getFloatArrayAttr(node,tag,v); },
                 encode( node, tag, v ) {        Xml.setFloatArrayAttr(node,tag,v); } },
  'string[]' : { decode( node, tag, v ) { return Xml.getStringArrayAttr(node,tag,v); },
                 encode( node, tag, v ) {        Xml.setStringArrayAttr(node,tag,v); } }
};

Xml.getParser = function( type ) {
  if( typeof type === 'string' ) {
    return Xml.Parser[type];
  }
  else {
    return Xml.makeObjectParser(type);
  }
};

Xml.parseProperty = function( node, type, name ) {
  const p = type.prototype.Properties[name], t = p.type;
  if( p.parser || typeof t === 'string' ) {
    const tag = p.tag || Xml.toXmlCase(name);
    return p.parser ? p.parser.decode(node,tag,p.def)
                    : Xml.Parser[t].decode(node,tag,p.def);
  }
  else {
    return t.fromXml ? t.fromXml(node,p.def) : t.parseXml(node,p.def);
  }
};
Xml.parseProperties = function( node, type, r = {} ) {
  forOwn( type.prototype.Properties, (prop,name) => {
    if( ! prop.manualParsing ) {
      r[name] = Xml.parseProperty(node,type,name);
    }
  });
  return r;
};

Xml.setupProperty = function(node,obj,name ) {
  const p = obj.Properties[name], t = p.type;
  const val = obj[p.member];
  if( val !== null && !p.doNotEncode ) {
    if( p.parser || typeof t === 'string' ) {
      if( !p.dropDef || ! ( val == p.def || (val.__CxArray && val.length===0 && p.def !== undefined && p.def.length===0) ) ) {
        const tag = p.tag || Xml.toXmlCase(name);
        if( p.parser ) {
          p.parser.encode(node,tag,val);
        }
        else {
          Xml.Parser[t].encode(node,tag,val);
        }
      }
    }
    else if( val.buildXml ) {
      val.buildXml(node) ;
    }
    else if( !p.dropDef || val != p.def ) {
      val.setupXml(node) ;
    }
  }
};
Xml.setupProperties = function(node,obj) {
  var sortedKeys = Object.keys(obj.Properties);
  sortedKeys.sort();
  forEach( sortedKeys, propName => {
    if( ! obj.Properties[propName].manualParsing ) {
      Xml.setupProperty(node,obj,propName);
    }
  });
};
Xml.addDefaultParseXml = function(Type) {
  if( ! Type.parseXml ) { // Allow user to define its own
    Type.parseXml = function( node ) { return new Type( Xml.parseProperties(node,Type) ); };
  }
};
Xml.addDefaultFromXml = function(Type,tag) {
  if( ! Type.fromXml ) { // Allow user to define its own
    Type.fromXml = function( node ) { return Xml.getObject(node,Type,tag); };
  }
  Xml.addDefaultParseXml(Type);
};

Xml.addDefaultSetupXml = function(Type) {
  const proto = Type.prototype;
  if( ! proto.setupXml ) { // Allow user to define its own
    proto.setupXml = function( node ) { return Xml.setupProperties(node,this); };
  }
};
Xml.addDefaultBuildXml = function(Type,tag) {
  const proto = Type.prototype;
  if( ! proto.buildXml ) { // Allow user to define its own
    proto.buildXml = function(parentNode ) { return Xml.addObject(parentNode,this,tag); };
  }
  Xml.addDefaultSetupXml(Type);
};

Xml.addFromBuild = function(Type,tag) {
  Xml.addDefaultBuildXml(Type,tag);
  Xml.addDefaultFromXml (Type,tag);
};
Xml.addParseSetup = function(Type) {
  Xml.addDefaultParseXml(Type);
  Xml.addDefaultSetupXml(Type);
};

Xml.addToXml = function(Type) {
  const proto = Type.prototype;
  if( ! proto.toXml ) { // Allow user to define its own
    proto.toXml = function() {
      const xml = Xml.fromString('<Cdl></Cdl>');
      this.buildXml(xml.documentElement);
      return xml;
    };
  }
  if( ! proto.toXmlString ) { // Allow user to define its own
    proto.toXmlString = function() {
      return Xml.toString( this.toXml() );
    };
  }
};

Xml.toXmlCase = function( string ) {
  return string.charAt(0).toUpperCase() + string.substr(1);
};

Xml.toObjectCase = function( string ) {
  return string.charAt(0).toLowerCase() + string.substr(1);
};


// Helpers for objects implementing setupXml and parseXml

Xml.addObject = function( parentNode, object, tag ) {
  const node = Xml.appendElement(parentNode,tag) ;
  object.setupXml(node) ;
  return node;
};
Xml.addObjectArray = function( parentNode, objectArray ) {
  forEach( objectArray, object => {
    object.buildXml(parentNode);
  });
};

Xml.getObject = function( parentNode, object, tag ) {
  const node = Xml.child(parentNode,tag);
  if( node ) {
    return object.parseXml(node);
  }
  return null ;
};

// Decode nodes of the form
// <tag/>
// <tag/>
// <tag/>
Xml.getObjectArray = function(parentNode,type,tag) { // [TODO][XML]
  const list = [] ;
  forEach( children_(parentNode), node => {
    const { nodeName } = node;
    if( nodeName && ( !tag || nodeName === tag )) {
      const obj = type.parseXml(node);
      if( obj ) {
        list.push( obj );
      }
    }
  });
  return list ;
};



/** WEBPACK FOOTER **
 ** ./src/cdl/util/xml.js
 **/